import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Tag, Users, Headphones, SlidersHorizontal, X, ShoppingCart } from 'lucide-react';
import Navbar from './components/ui/navbar';
import { useCart } from './contexts/CartContext';

interface Product {
  id: number;
  name: string;
  price: string;
  image: string;
}

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState<number | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const navigate = useNavigate();
  const { addToCart, getCartCount } = useCart();

  // Generate more sample products for infinite scroll
  const generateProducts = (startId: number, count: number): Product[] => {
    const images = [
      'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1567016432779-094069958ea5?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1550581190-9c1c48d21d6c?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1540574163026-643ea20ade25?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd?w=400&h=300&fit=crop',
    ];

    const names = [
      'Modern Sofa', 'Elegant Table Lamp', 'Dining Table Set', 'Accent Chair',
      'Floor Lamp', 'Bookshelf Unit', 'Coffee Table', 'Bed Frame',
      'Desk Chair', 'Side Table', 'Pendant Light', 'Ottoman',
      'Console Table', 'Bar Stool', 'Chandelier', 'Armchair'
    ];

    return Array.from({ length: count }, (_, i) => ({
      id: startId + i,
      name: names[(startId + i - 1) % names.length],
      price: `$${Math.floor(Math.random() * 2000) + 200}`,
      image: images[(startId + i - 1) % images.length]
    }));
  };

  // Initial load
  useEffect(() => {
    const initialProducts = generateProducts(1, 20);
    setProducts(initialProducts);
    setFilteredProducts(initialProducts);
  }, []);

  // Handle scroll for search bar animation
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load more products
  const loadMoreProducts = useCallback(() => {
    if (loading || !hasMore) return;

    setLoading(true);
    setTimeout(() => {
      const newProducts = generateProducts(products.length + 1, 20);
      setProducts(prev => [...prev, ...newProducts]);
      setFilteredProducts(prev => [...prev, ...newProducts]);
      setLoading(false);

      // Simulate ending after 100 products
      if (products.length + newProducts.length >= 100) {
        setHasMore(false);
      }
    }, 1000);
  }, [products.length, loading, hasMore]);

  // Intersection observer for infinite scroll
  const lastProductRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreProducts();
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, hasMore, loadMoreProducts]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setFilteredProducts(products);
      return;
    }

    const filtered = products.filter(product => {
      const searchLower = searchQuery.toLowerCase();
      return product.name.toLowerCase().includes(searchLower);
    });

    setFilteredProducts(filtered);
  };

  const filterOptions = [
    { value: 'modern', label: 'Modern' },
    { value: 'vintage', label: 'Vintage' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: 'sale', label: 'On Sale' },
    { value: 'new', label: 'New Arrivals' },
  ];

  const toggleFilter = (filter: string) => {
    setActiveFilters(prev =>
      prev.includes(filter)
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const clearAllFilters = () => {
    setActiveFilters([]);
    setPriceRange({ min: '', max: '' });
  };

  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Fleait logo",
      title: "Fleait"
    },
    menu: [
      { title: "Home", url: "/" },
      { title: "Products", url: "/products" },
      {
        title: "Categories",
        url: "#",
        items: [
          {
            title: "Living Room",
            description: "Sofas, coffee tables, chairs, and living room furniture",
            icon: <ShoppingBag className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Lighting",
            description: "Lamps, chandeliers, and modern lighting fixtures",
            icon: <Tag className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "For Creators",
        url: "#",
        items: [
          {
            title: "Start Selling",
            description: "Join Flea and sell with 0% commission",
            icon: <Users className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Support",
            description: "Get help setting up your creator space",
            icon: <Headphones className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "About",
        url: "/about",
      },
    ],
    mobileExtraLinks: [
      { name: "Sell on Flea", url: "#" },
      { name: "FAQ", url: "#" },
      { name: "Shipping", url: "#" },
    ],
    auth: {
      login: { text: "Sign In", url: "/signin" },
      signup: { text: "Create Space", url: "/create-space" },
    },
  };

  return (
    <div className="scroll-snap-container">
      {/* Fixed Floating Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-3 sm:pt-6 px-2 sm:px-4 pointer-events-none animate-navbar-float">
        <div className="max-w-7xl w-full bg-[rgb(255, 255, 255)] border-2 border-[rgb(0, 0, 0)] rounded-[20px] sm:rounded-[40px] px-3 sm:px-8 pointer-events-auto transition-all duration-500 hover:scale-[1.01] relative overflow-hidden before:absolute before:inset-0 before:z-[-1] before:opacity-60 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]">
          <Navbar {...navbarConfig} cartCount={getCartCount()} />
        </div>
      </div>

      {/* Content Wrapper with Rounded Corners */}
      <div className="page-content-wrapper pt-24">
        {/* Floating Search & Filter Bar */}
        <div className={`fixed top-[90px] sm:top-[110px] z-40 flex px-2 sm:px-4 pointer-events-none transition-all duration-700 ease-out ${
          isScrolled && !isSearchHovered ? 'right-4 sm:right-8 left-auto justify-end' : 'left-0 right-0 justify-center'
        }`}>
          <div
            className={`transition-all duration-700 ease-out pointer-events-auto ${
              isScrolled && !isSearchHovered
                ? 'w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#458aff] to-[#6ba3ff] backdrop-blur-xl rounded-full shadow-2xl flex items-center justify-center border-2 border-white/30'
                : 'max-w-7xl w-full bg-[#f5f3f0] backdrop-blur-xl rounded-[20px] sm:rounded-[40px] px-3 sm:px-6 py-3 sm:py-4 shadow-lg'
            }`}
            onMouseEnter={() => setIsSearchHovered(true)}
            onMouseLeave={() => setIsSearchHovered(false)}
          >
            <div className={`flex items-center gap-3 transition-all duration-700 ${isScrolled && !isSearchHovered ? 'w-auto' : 'w-full'}`}>
              {/* Filter Toggle Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 rounded-full transition-all duration-700 ${
                  showFilters || activeFilters.length > 0
                    ? 'bg-black text-[#f5f3f0]'
                    : 'bg-[#f5f3f0] text-black hover:bg-gray-100'
                } ${
                  isScrolled && !isSearchHovered
                    ? 'opacity-0 invisible w-0 px-0 overflow-hidden pointer-events-none'
                    : 'opacity-100 visible px-4 py-2.5'
                }`}
              >
                <SlidersHorizontal size={18} />
                <span className="hidden sm:inline whitespace-nowrap">Filters</span>
                {activeFilters.length > 0 && (
                  <span className="bg-[#f5f3f0] text-black text-xs px-2 py-0.5 rounded-full font-medium">
                    {activeFilters.length}
                  </span>
                )}
              </button>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className={`relative transition-all duration-700 ${
                isScrolled && !isSearchHovered ? 'w-auto flex-shrink-0' : 'flex-1'
              }`}>
                <button
                  type="submit"
                  className={`transition-all duration-700 rounded-full flex items-center justify-center ${
                    isScrolled && !isSearchHovered
                      ? 'relative w-auto h-auto p-0 bg-transparent hover:scale-110'
                      : 'absolute top-1/2 -translate-y-1/2 right-2 p-2 hover:bg-gray-200'
                  }`}
                >
                  <Search size={20} className={`transition-colors duration-700 ${
                    isScrolled && !isSearchHovered ? 'text-white' : 'text-black'
                  }`} />
                </button>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for unique finds..."
                  className={`transition-all duration-700 bg-[#f5f3f0] rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    isScrolled && !isSearchHovered
                      ? 'opacity-0 invisible w-0 px-0 py-0 border-0'
                      : 'opacity-100 visible w-full px-4 py-2.5 pr-10'
                  }`}
                />
              </form>
            </div>
          </div>
        </div>

        {/* Spacer for floating search bar */}
        <div className="h-20"></div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
            <div className="bg-[#f5f3f0] backdrop-blur-xl rounded-[40px] p-6 shadow-lg">
              <div className="flex flex-wrap gap-3 items-start">

                {/* Filter Options */}
                <div className="flex-1 min-w-[200px]">
                  <h4 className="text-xs font-medium text-black mb-2 uppercase tracking-wide">Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {filterOptions.map((filter) => (
                      <button
                        key={filter.value}
                        onClick={() => toggleFilter(filter.value)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          activeFilters.includes(filter.value)
                            ? 'bg-black text-[#f5f3f0]'
                            : 'bg-white text-black hover:bg-gray-100'
                        }`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className="flex-1 min-w-[200px]">
                  <h4 className="text-xs font-medium text-black mb-2 uppercase tracking-wide">Price Range</h4>
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      placeholder="Min"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                      className="w-24 px-3 py-1.5 bg-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <span className="text-black text-sm">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                      className="w-24 px-3 py-1.5 bg-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                </div>

                {/* Clear Filters */}
                {(activeFilters.length > 0 || priceRange.min || priceRange.max) && (
                  <button
                    onClick={clearAllFilters}
                    className="self-end px-3 py-1.5 text-sm text-black hover:text-black font-medium flex items-center gap-1"
                  >
                    <X size={14} />
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Products Grid with Smaller Tiles */}
        <section className="py-8 bg-[#f5f3f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProducts.length === 0 && searchQuery ? (
            <div className="text-center py-16">
              <div className="flex justify-center mb-4">
                <Search size={64} className="text-black" />
              </div>
              <h3 className="text-2xl font-light text-black mb-4">No results found</h3>
              <p className="text-black mb-8">Try adjusting your search terms or browse our collection</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilteredProducts(products);
                }}
                className="bg-black text-[#f5f3f0] px-8 py-3 rounded-lg hover:bg-black transition-colors"
              >
                View All Products
              </button>
            </div>
          ) : (
            <>
              <div className="bento-grid">
                {filteredProducts.map((product, index) => {
                  const isLastProduct = index === filteredProducts.length - 1;
                  const isExpanded = expandedProductId === product.id;

                  // Create dynamic bento layout patterns for Pinterest-like variety
                  const getBentoClass = (index: number) => {
                    const patterns = [
                      'bento-large', // Large featured card
                      'bento-tall', // Tall card
                      'bento-wide', // Wide card
                      'bento-small', // Regular card
                      'bento-small', // Regular card
                      'bento-tall', // Tall card
                      'bento-wide', // Wide card
                      'bento-small', // Regular card
                    ];
                    return patterns[index % patterns.length];
                  };

                  const bentoClass = getBentoClass(index);

                  return (
                    <div
                      key={product.id}
                      ref={isLastProduct ? lastProductRef : null}
                      className={`bento-card ${
                        isExpanded
                          ? 'bento-expanded'
                          : expandedProductId !== null
                            ? 'bento-small'
                            : bentoClass
                      } group cursor-pointer transition-all duration-700 ease-out ${
                        isExpanded ? 'z-50' : 'z-0'
                      }`}
                      onClick={() => {
                        if (isExpanded) {
                          setExpandedProductId(null);
                        } else {
                          setExpandedProductId(product.id);
                        }
                      }}
                    >
                      <div className={`glass-card h-full relative overflow-hidden ${
                        isExpanded ? 'shadow-2xl' : ''
                      }`}>
                        {/* Background Image with Overlay */}
                        <div className="absolute inset-0">
                          <img
                            src={product.image}
                            alt={product.name}
                            className={`w-full h-full object-cover transition-all duration-700 ${
                              isExpanded ? 'scale-100' : 'group-hover:scale-110'
                            }`}
                          />
                          <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent transition-opacity duration-300 ${
                            isExpanded ? 'opacity-95' : 'opacity-80 group-hover:opacity-90'
                          }`}></div>
                        </div>

                        {/* Content Overlay */}
                        <div className={`relative h-full flex flex-col justify-end transition-all duration-700 text-[#f5f3f0] ${
                          isExpanded ? 'p-8' : 'p-3'
                        }`}>
                          <div className={`transition-all duration-700 ${isExpanded ? 'space-y-4' : 'space-y-1.5'}`}>
                            <h3 className={`font-semibold leading-tight text-shadow transition-all duration-700 ${
                              isExpanded ? 'text-3xl line-clamp-none' : 'text-sm line-clamp-2'
                            }`}>
                              {product.name}
                            </h3>

                            {isExpanded && (
                              <p className="text-base text-[#f5f3f0]/90 leading-relaxed animate-fadeIn">
                                Crafted with attention to detail and quality materials. This piece combines functionality
                                with elegant design, perfect for transforming your living space.
                              </p>
                            )}

                            <div className="flex items-center justify-between">
                              <p className={`font-bold text-[#f5f3f0] transition-all duration-700 ${
                                isExpanded ? 'text-3xl' : 'text-lg'
                              }`}>
                                {product.price}
                              </p>
                              <div className={`flex gap-2 transition-all duration-300 ${
                                isExpanded
                                  ? 'opacity-100 translate-y-0'
                                  : 'opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0'
                              }`}>
                                <button
                                  className={`bg-white text-black font-medium rounded-full transition-all duration-300 hover:scale-110 hover:shadow-lg flex items-center justify-center ${
                                    isExpanded
                                      ? 'w-12 h-12'
                                      : 'w-8 h-8'
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addToCart(product);
                                  }}
                                  title="Add to Bag"
                                >
                                  <ShoppingCart size={isExpanded ? 20 : 16} />
                                </button>
                                <button
                                  className={`bg-[#f5f3f0] text-black font-medium rounded-full transition-all duration-300 ${
                                    isExpanded
                                      ? 'py-3 px-8 text-base'
                                      : 'py-1 px-3 text-xs'
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isExpanded) {
                                      navigate(`/product/${product.id}`);
                                    }
                                  }}
                                >
                                  {isExpanded ? 'View Details' : 'View'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Loading Indicator */}
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="flex items-center gap-3 text-black">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                    <span className="text-sm font-medium">Loading more products...</span>
                  </div>
                </div>
              )}

              {/* End of Products */}
              {!hasMore && filteredProducts.length > 0 && (
                <div className="text-center py-12">
                  <p className="text-black text-sm">You've reached the end of our collection</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

        {/* Footer */}
        <footer className="bg-[rgba(69,138,255,0.8)] text-black py-16 relative overflow-hidden before:absolute before:inset-0 before:z-[-1] before:opacity-60 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-2xl font-logo mb-2 text-black">Fleait</h3>
                <p className="text-black text-base mb-4">New York City</p>
                <p className="text-black text-lg">
                  Curating beautiful furniture, one piece at a time.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4 text-black">Quick Links</h4>
                <ul className="space-y-2">
                  <li><a href="/" className="text-black hover:text-black transition-colors">Home</a></li>
                  <li><a href="/products" className="text-black hover:text-black transition-colors">Categories</a></li>
                  <li><a href="/about" className="text-black hover:text-black transition-colors">About</a></li>
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Contact</button></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4 text-black">Support</h4>
                <ul className="space-y-2">
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Help Center</button></li>
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Shipping</button></li>
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Returns</button></li>
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Privacy</button></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-black mt-12 pt-6 text-center">
              <p className="text-black text-sm">© 2024 Fleait. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ProductsPage;
